import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "workspace-tasks-tab" }
const _hoisted_2 = {
  key: 0,
  class: "workspace-toolbar"
}
const _hoisted_3 = { class: "search-box" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 0,
  class: "filter-panel"
}
const _hoisted_6 = { class: "filter-panel-header" }
const _hoisted_7 = { class: "filter-grid" }
const _hoisted_8 = { class: "filter-group" }
const _hoisted_9 = { value: null }
const _hoisted_10 = { value: "PENDING" }
const _hoisted_11 = { value: "IN_PROGRESS" }
const _hoisted_12 = { value: "COMPLETED" }
const _hoisted_13 = { value: "ON_HOLD" }
const _hoisted_14 = { value: "CANCELLED" }
const _hoisted_15 = { class: "filter-group" }
const _hoisted_16 = ["placeholder"]
const _hoisted_17 = ["placeholder"]
const _hoisted_18 = { class: "filter-group" }
const _hoisted_19 = ["placeholder"]
const _hoisted_20 = ["placeholder"]
const _hoisted_21 = { class: "filter-group" }
const _hoisted_22 = { value: null }
const _hoisted_23 = { value: "assigned" }
const _hoisted_24 = { value: "unassigned" }
const _hoisted_25 = {
  key: 0,
  class: "filter-group"
}
const _hoisted_26 = { value: null }
const _hoisted_27 = ["value"]
const _hoisted_28 = { class: "filter-group" }
const _hoisted_29 = { value: null }
const _hoisted_30 = ["value"]
const _hoisted_31 = { class: "data-grid" }

import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import { usePermissions } from '@/composables/usePermissions'
import AppDataGrid from '@/components/base/AppDataGrid.vue'
import TaskFormModal from '../modals/TaskFormModal.vue'
import TaskStatusTransitionModal from '@/components/project/task/TaskStatusTransitionModal.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import projectTasksService from '@/services/projectTasks.service'
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils'
import { formatDate } from '@/utils/formatters'

// Interfaces
interface Phase {
  id?: number
  projectId: number
  name: string
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
  actualStartDate?: string
  actualEndDate?: string
  dependencyPhaseId?: number
  dependencyPhaseName?: string
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'
  isDelivered?: boolean
  deliveredDate?: string
  createdBy?: number
  createdAt?: string
  updatedAt?: string
}

interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: string
  email: string
  phone?: string
  address?: string
  createdAt?: string
  updatedAt?: string
}

interface Task {
  id?: number
  projectId?: number
  name: string
  description?: string
  amount: number
  startDate?: string
  endDate?: string
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD'
  assignedTo?: string
  createdBy?: number
  updatedBy?: number
  createdAt?: string
  updatedAt?: string
  intervenantName?: string
  intervenantId?: number
  phaseId?: number
  phaseName?: string
}

interface Project {
  id?: number
  multiPhase?: boolean
  [key: string]: any
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkspaceTasksTab',
  props: {
    projectId: {},
    tasks: {},
    allIntervenants: {},
    phases: {},
    project: {},
    showSearch: { type: Boolean },
    showFilters: { type: Boolean },
    showPhaseColumn: { type: Boolean },
    filteredTasks: {}
  },
  emits: ["taskSelected", "taskCreated", "taskUpdated", "taskDeleted"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

const props = __props

const emit = __emit

// Provide default values for optional props
const phases = computed(() => props.phases || [])
const showSearch = computed(() => props.showSearch !== false) // Default to true
const showFilters = computed(() => props.showFilters === true) // Default to false
const showPhaseColumn = computed(() => props.showPhaseColumn === true) // Default to false

// State
const selectedTask = ref<Task | null>(null)
const searchQuery = ref('')
const showAdvancedFilters = ref(false)
const showTaskModal = ref(false)
const showTaskStatusModal = ref(false)
const editingTask = ref<Task | null>(null)
const selectedTaskForStatusChange = ref<Task | null>(null)
const showDeleteConfirm = ref(false)
const deleteTaskId = ref<number | null>(null)
const deleteTaskName = ref('')

// Filter state
const filters = ref({
  status: null as string | null,
  startDate: '',
  endDate: '',
  minAmount: null as number | null,
  maxAmount: null as number | null,
  assignment: null as string | null,
  phaseId: null as number | null,
  intervenantId: null as number | null
})

// Pagination configuration for AppDataGrid
const itemsPerPageOptions = [5, 10, 25, 50, 100]

// Reactive formatted amounts
const formattedAmounts = ref<Record<string, string>>({})

// Computed - Get tasks to display (either filtered or all)
const baseTasks = computed(() => {
  return props.filteredTasks || props.tasks
})

// Computed - Apply search and advanced filters
const searchAndFilteredTasks = computed(() => {
  let result = [...baseTasks.value]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((task: Task) =>
      task.name?.toLowerCase().includes(query) ||
      task.description?.toLowerCase().includes(query) ||
      task.intervenantName?.toLowerCase().includes(query)
    )
  }

  // Apply advanced filters (only if showFilters is true)
  if (showFilters.value) {
    // Status filter
    if (filters.value.status) {
      result = result.filter((task: Task) => task.status === filters.value.status)
    }

    // Date range filter
    if (filters.value.startDate) {
      result = result.filter((task: Task) => 
        task.startDate && task.startDate >= filters.value.startDate
      )
    }
    if (filters.value.endDate) {
      result = result.filter((task: Task) => 
        task.endDate && task.endDate <= filters.value.endDate
      )
    }

    // Budget range filter
    if (filters.value.minAmount !== null) {
      result = result.filter((task: Task) => task.amount >= filters.value.minAmount!)
    }
    if (filters.value.maxAmount !== null) {
      result = result.filter((task: Task) => task.amount <= filters.value.maxAmount!)
    }

    // Assignment filter
    if (filters.value.assignment === 'assigned') {
      result = result.filter((task: Task) => task.intervenantId)
    } else if (filters.value.assignment === 'unassigned') {
      result = result.filter((task: Task) => !task.intervenantId)
    }

    // Phase filter
    if (filters.value.phaseId) {
      result = result.filter((task: Task) => task.phaseId === filters.value.phaseId)
    }

    // Intervenant filter
    if (filters.value.intervenantId) {
      result = result.filter((task: Task) => task.intervenantId === filters.value.intervenantId)
    }
  }

  return result
})

// Computed - Check if any filters are active
const hasActiveFilters = computed(() => {
  return filters.value.status ||
    filters.value.startDate ||
    filters.value.endDate ||
    filters.value.minAmount !== null ||
    filters.value.maxAmount !== null ||
    filters.value.assignment ||
    filters.value.phaseId ||
    filters.value.intervenantId
})

// Methods
const selectTask = (task: Task) => {
  selectedTask.value = task
  emit('taskSelected', task)
}

const editTask = (task: Task) => {
  editingTask.value = { ...task }
  showTaskModal.value = true
}

const openTaskStatusModal = (task: Task) => {
  selectedTaskForStatusChange.value = task
  showTaskStatusModal.value = true
}

const handleTaskStatusChanged = () => {
  showTaskStatusModal.value = false
  selectedTaskForStatusChange.value = null
  emit('taskUpdated')
}

const deleteTask = (id: number) => {
  const task = searchAndFilteredTasks.value.find((t: Task) => t.id === id)
  deleteTaskId.value = id
  deleteTaskName.value = task?.name || ''
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  if (deleteTaskId.value === null) return
  
  try {
    await projectTasksService.deleteTask(
      props.projectId.toString(),
      deleteTaskId.value.toString()
    )
    notifySuccess(t('workspace.success.taskDeleted'))
    emit('taskDeleted')
  } catch (error) {
    notifyError(t('workspace.errors.taskDeleteFailed'))
  } finally {
    cancelDelete()
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
  deleteTaskId.value = null
  deleteTaskName.value = ''
}

const closeTaskModal = () => {
  showTaskModal.value = false
  editingTask.value = null
}

const handleTaskSaved = () => {
  closeTaskModal()
  if (editingTask.value?.id) {
    emit('taskUpdated')
  } else {
    emit('taskCreated')
  }
}

const clearFilters = () => {
  filters.value = {
    status: null,
    startDate: '',
    endDate: '',
    minAmount: null,
    maxAmount: null,
    assignment: null,
    phaseId: null,
    intervenantId: null
  }
}

// Grid Configuration
const { hasPermissionForProject, PermissionType } = usePermissions()

// Helper functions for cell rendering
const getStatusDotColor = (status: string): string => {
  switch (status) {
    case 'completed': return '#10b981'
    case 'in_progress': return '#3b82f6'
    case 'on_hold': return '#f59e0b'
    case 'cancelled': return '#ef4444'
    case 'pending': return '#9ca3af'
    default: return '#9ca3af'
  }
}

const getStatusBadgeColors = (status: string) => {
  switch (status) {
    case 'completed': return { bg: '#d1fae5', text: '#065f46' }
    case 'in_progress': return { bg: '#dbeafe', text: '#1e40af' }
    case 'on_hold': return { bg: '#fed7aa', text: '#92400e' }
    case 'cancelled': return { bg: '#fee2e2', text: '#991b1b' }
    case 'pending': return { bg: '#f3f4f6', text: '#374151' }
    default: return { bg: '#f3f4f6', text: '#374151' }
  }
}

// Custom cell renderers for tasks grid
const taskCellRenderers: Record<string, (params: any) => string> = {
  name: (params: any) => {
    const status = params.data.statusRaw?.toLowerCase() || 'pending'
    return `
      <div style="display: flex; align-items: center; gap: 12px;">
        <span style="
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${getStatusDotColor(status)};
          flex-shrink: 0;
        "></span>
        <strong style="font-weight: 600; color: #1f2937;">${params.value}</strong>
      </div>
    `
  },
  
  intervenant: (params: any) => {
    if (!params.value) return '<span style="color: #9ca3af;">-</span>'
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  phase: (params: any) => {
    if (!params.value) {
      return `<span style="color: #9ca3af; font-style: italic;">${t('workspace.tasks.unassigned')}</span>`
    }
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  amount: (params: any) => {
    if (!params.value) return '-'
    return `
      <span style="
        font-weight: 600;
        color: #059669;
        font-size: 15px;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      ">${params.value}</span>
    `
  },
  
  dates: (params: any) => {
    const startDate = params.data.startDate ? formatDate(params.data.startDate) : '-'
    const endDate = params.data.endDate ? formatDate(params.data.endDate) : '-'
    return `
      <div style="display: flex; flex-direction: column; gap: 2px; font-size: 0.875rem;">
        <div style="color: #374151;">${startDate}</div>
        <div style="color: #9ca3af; font-size: 0.75rem;">→</div>
        <div style="color: #374151;">${endDate}</div>
      </div>
    `
  },
  
  status: (params: any) => {
    if (!params.value) return ''
    const status = params.data.statusRaw?.toLowerCase() || 'pending'
    const colors = getStatusBadgeColors(status)
    return `
      <span style="
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 6px 14px;
        border-radius: 20px;
        font-weight: 600;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        background-color: ${colors.bg};
        color: ${colors.text};
        white-space: nowrap;
      ">
        <span style="width: 6px; height: 6px; border-radius: 50%; background-color: currentColor;"></span>
        ${params.value}
      </span>
    `
  }
}

// Prepare task data for grid
const taskGridData = computed(() => {
  return searchAndFilteredTasks.value.map((task: Task) => ({
    id: task.id,
    name: task.name,
    intervenant: task.intervenantName || null,
    phase: task.phaseName || null,
    amount: getFormattedAmount(`task-${task.id}`),
    dates: `${task.startDate || ''} - ${task.endDate || ''}`,
    startDate: task.startDate,
    endDate: task.endDate,
    status: t(`tasks.status.${task.status}`),
    statusRaw: task.status,
    _originalData: task
  }))
})

// Column definitions for tasks grid
const taskColumnDefs = computed(() => {
  const columns = [
    {
      field: 'name',
      headerName: t('workspace.tasks.name'),
      cellRenderer: taskCellRenderers.name,
      minWidth: 180,
      flex: 1,
      sortable: true,
      wrapText: false,
      autoHeight: false
    },
    {
      field: 'intervenant',
      headerName: t('workspace.tasks.intervenant'),
      cellRenderer: taskCellRenderers.intervenant,
      width: 180,
      sortable: true,
      wrapText: false,
      autoHeight: false
    }
  ]
  
  // Add phase column conditionally
  if (showPhaseColumn.value) {
    columns.push({
      field: 'phase',
      headerName: t('workspace.tasks.phase'),
      cellRenderer: taskCellRenderers.phase,
      width: 160,
      sortable: true,
      wrapText: false,
      autoHeight: false
    })
  }
  
  columns.push(
    {
      field: 'amount',
      headerName: t('workspace.tasks.amount'),
      cellRenderer: taskCellRenderers.amount,
      width: 140,
      sortable: true,
      wrapText: false,
      autoHeight: false
    },
    {
      field: 'dates',
      headerName: t('workspace.tasks.dates'),
      cellRenderer: taskCellRenderers.dates,
      width: 180,
      sortable: false,
      wrapText: false,
      autoHeight: false
    },
    {
      field: 'status',
      headerName: t('workspace.tasks.status'),
      cellRenderer: taskCellRenderers.status,
      width: 200,
      sortable: true,
      wrapText: false,
      autoHeight: false
    }
  )
  
  return columns
})

// Action definitions for tasks grid
const taskActions = computed(() => {
  const actions = []
  
  // Edit button
  if (hasPermissionForProject(PermissionType.UPDATE_TASK, props.projectId).value) {
    actions.push({
      name: 'edit',
      label: t('common.edit'),
      icon: 'fas fa-edit',
      class: 'edit-btn',
      handler: (data: any) => editTask(data._originalData)
    })
  }
  
  // Change Status button
  if (hasPermissionForProject(PermissionType.UPDATE_TASK, props.projectId).value) {
    actions.push({
      name: 'changeStatus',
      label: t('taskWorkspace.statusTransition.changeStatus'),
      icon: 'fas fa-exchange-alt',
      class: 'edit-btn',
      handler: (data: any) => openTaskStatusModal(data._originalData)
    })
  }
  
  // Delete button
  if (hasPermissionForProject(PermissionType.DELETE_TASK, props.projectId).value) {
    actions.push({
      name: 'delete',
      label: t('common.delete'),
      icon: 'fas fa-trash',
      class: 'delete-btn',
      handler: (data: any) => deleteTask(data.id)
    })
  }
  
  return actions
})

// Handle task grid actions
const handleTaskAction = (action: any, data: any) => {
  action.handler(data)
}

// Handle task row clicks
const handleTaskRowClick = (event: any) => {
  selectTask(event.data._originalData)
}

const formatCurrency = async (amount: number, key?: string) => {
  if (amount === null || amount === undefined) return '-'
  try {
    const formatted = await formatCurrencyWithUserPreference(amount)
    if (key) {
      formattedAmounts.value[key] = formatted
    }
    return formatted
  } catch (err) {
    console.warn('Error formatting currency:', err)
    return amount.toString()
  }
}

const getFormattedAmount = (key: string) => {
  return formattedAmounts.value[key] || '...'
}

// Format amounts when tasks change
const formatAllAmounts = async () => {
  try {
    for (const task of searchAndFilteredTasks.value) {
      if (task.id && task.amount !== undefined) {
        const key = `task-${task.id}`
        await formatCurrency(task.amount, key)
      }
    }
  } catch (error) {
    console.warn('Error formatting amounts:', error)
  }
}

// Watch for prop changes to reformat amounts
watch(() => props.tasks, formatAllAmounts, { immediate: true, deep: true })
watch(() => props.filteredTasks, formatAllAmounts, { immediate: true, deep: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showSearch.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[10] || (_cache[10] = _createElementVNode("span", { class: "search-icon" }, "🔍", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              placeholder: _ctx.$t('workspace.search'),
              class: "search-input"
            }, null, 8, _hoisted_4), [
              [_vModelText, searchQuery.value]
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "slide-down" }, {
      default: _withCtx(() => [
        (showFilters.value && showAdvancedFilters.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("h3", null, _toDisplayString(_ctx.$t('workspace.filters.title')), 1),
                (hasActiveFilters.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "btn-text",
                      onClick: clearFilters
                    }, _toDisplayString(_ctx.$t('workspace.filters.clearAll')), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.status')), 1),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.status) = $event)),
                    class: "filter-select"
                  }, [
                    _createElementVNode("option", _hoisted_9, _toDisplayString(_ctx.$t('workspace.filters.allStatuses')), 1),
                    _createElementVNode("option", _hoisted_10, _toDisplayString(_ctx.$t('tasks.status.PENDING')), 1),
                    _createElementVNode("option", _hoisted_11, _toDisplayString(_ctx.$t('tasks.status.IN_PROGRESS')), 1),
                    _createElementVNode("option", _hoisted_12, _toDisplayString(_ctx.$t('tasks.status.COMPLETED')), 1),
                    _createElementVNode("option", _hoisted_13, _toDisplayString(_ctx.$t('tasks.status.ON_HOLD')), 1),
                    _createElementVNode("option", _hoisted_14, _toDisplayString(_ctx.$t('tasks.status.CANCELLED')), 1)
                  ], 512), [
                    [_vModelSelect, filters.value.status]
                  ])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.dateRange')), 1),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.startDate) = $event)),
                    type: "date",
                    placeholder: _ctx.$t('workspace.filters.startDate'),
                    class: "filter-input"
                  }, null, 8, _hoisted_16), [
                    [_vModelText, filters.value.startDate]
                  ]),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.value.endDate) = $event)),
                    type: "date",
                    placeholder: _ctx.$t('workspace.filters.endDate'),
                    class: "filter-input"
                  }, null, 8, _hoisted_17), [
                    [_vModelText, filters.value.endDate]
                  ])
                ]),
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.budgetRange')), 1),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.minAmount) = $event)),
                    type: "number",
                    placeholder: _ctx.$t('workspace.filters.minAmount'),
                    class: "filter-input"
                  }, null, 8, _hoisted_19), [
                    [
                      _vModelText,
                      filters.value.minAmount,
                      void 0,
                      { number: true }
                    ]
                  ]),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.value.maxAmount) = $event)),
                    type: "number",
                    placeholder: _ctx.$t('workspace.filters.maxAmount'),
                    class: "filter-input"
                  }, null, 8, _hoisted_20), [
                    [
                      _vModelText,
                      filters.value.maxAmount,
                      void 0,
                      { number: true }
                    ]
                  ])
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.assignment')), 1),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.value.assignment) = $event)),
                    class: "filter-select"
                  }, [
                    _createElementVNode("option", _hoisted_22, _toDisplayString(_ctx.$t('workspace.filters.allTasks')), 1),
                    _createElementVNode("option", _hoisted_23, _toDisplayString(_ctx.$t('workspace.filters.assigned')), 1),
                    _createElementVNode("option", _hoisted_24, _toDisplayString(_ctx.$t('workspace.filters.unassigned')), 1)
                  ], 512), [
                    [_vModelSelect, filters.value.assignment]
                  ])
                ]),
                (showPhaseColumn.value && phases.value.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                      _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.phase')), 1),
                      _withDirectives(_createElementVNode("select", {
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((filters.value.phaseId) = $event)),
                        class: "filter-select"
                      }, [
                        _createElementVNode("option", _hoisted_26, _toDisplayString(_ctx.$t('workspace.filters.allPhases')), 1),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(phases.value, (phase) => {
                          return (_openBlock(), _createElementBlock("option", {
                            key: phase.id,
                            value: phase.id
                          }, _toDisplayString(phase.name), 9, _hoisted_27))
                        }), 128))
                      ], 512), [
                        [_vModelSelect, filters.value.phaseId]
                      ])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_28, [
                  _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.intervenant')), 1),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((filters.value.intervenantId) = $event)),
                    class: "filter-select"
                  }, [
                    _createElementVNode("option", _hoisted_29, _toDisplayString(_ctx.$t('workspace.filters.allIntervenants')), 1),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.allIntervenants, (intervenant) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: intervenant.id,
                        value: intervenant.id
                      }, _toDisplayString(intervenant.name), 9, _hoisted_30))
                    }), 128))
                  ], 512), [
                    [_vModelSelect, filters.value.intervenantId]
                  ])
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_31, [
      _createVNode(AppDataGrid, {
        "row-data": taskGridData.value,
        "column-defs": taskColumnDefs.value,
        actions: taskActions.value,
        pagination: true,
        "page-size": 10,
        "page-size-options": itemsPerPageOptions,
        "suppress-pagination-panel": false,
        "show-search": false,
        "grid-class": "tasks-grid",
        "grid-style": "width: 100%;",
        "dom-layout": "autoHeight",
        "default-col-def": { wrapText: false, autoHeight: false },
        onRowClicked: handleTaskRowClick,
        onActionClicked: handleTaskAction
      }, null, 8, ["row-data", "column-defs", "actions"])
    ]),
    _createVNode(TaskFormModal, {
      show: showTaskModal.value,
      task: editingTask.value,
      "project-id": _ctx.projectId,
      project: _ctx.project,
      phases: phases.value,
      intervenants: _ctx.allIntervenants,
      onClose: closeTaskModal,
      onSaved: handleTaskSaved
    }, null, 8, ["show", "task", "project-id", "project", "phases", "intervenants"]),
    (selectedTaskForStatusChange.value)
      ? (_openBlock(), _createBlock(TaskStatusTransitionModal, {
          key: 1,
          "is-visible": showTaskStatusModal.value,
          "task-id": selectedTaskForStatusChange.value.id!,
          "project-id": _ctx.projectId,
          "current-status": selectedTaskForStatusChange.value.status,
          onClose: _cache[9] || (_cache[9] = ($event: any) => {showTaskStatusModal.value = false; selectedTaskForStatusChange.value = null}),
          onSuccess: handleTaskStatusChanged
        }, null, 8, ["is-visible", "task-id", "project-id", "current-status"]))
      : _createCommentVNode("", true),
    (showDeleteConfirm.value)
      ? (_openBlock(), _createBlock(ConfirmModal, {
          key: 2,
          isVisible: showDeleteConfirm.value,
          title: _ctx.$t('tasks.deleteTask'),
          message: _ctx.$t('workspace.confirmDelete.task'),
          details: deleteTaskName.value,
          "confirm-text": _ctx.$t('common.delete'),
          "cancel-text": _ctx.$t('common.cancel'),
          type: "danger",
          onConfirm: confirmDelete,
          onCancel: cancelDelete
        }, null, 8, ["isVisible", "title", "message", "details", "confirm-text", "cancel-text"]))
      : _createCommentVNode("", true)
  ]))
}
}

})